import React, { useEffect, useState } from 'react';
import { api, colaOffline } from '../lib/api';
import { LectorQr, leerArchivo } from './Qr';
import { prepararFoto } from '../lib/qr';
import { PlanillaMovilidad, ReciboAd } from './Sustentos';
import { DocumentoImprimible } from './Documentos';
import { ConfirmarEliminar } from './Eliminar';
import {
  T, serif, fmt, Card, Chip, Boton, Campo, estiloInput,
  Titulo, Aviso, Vacio, Cargando,
} from '../lib/ui';

// ════════════════════════════════════════════════════════════════════
// PANEL
// ════════════════════════════════════════════════════════════════════
export function Panel({ usuario, irA }) {
  const [kpis, setKpis] = useState(null);
  const [encargos, setEncargos] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [k, e] = await Promise.all([api.kpis(), api.encargos(true)]);
        setKpis(k.porEstado);
        setEncargos(e.encargos);
      } catch { setKpis([]); }
    })();
  }, []);

  if (!kpis) return <Cargando />;

  const de = (estado) => kpis.find((k) => k.estado === estado) || { cantidad: 0, total: 0 };
  const porAprobar = de('enviado');
  const observados = de('observado');
  const saldoAbierto = encargos.reduce((s, e) => s + (e.saldo || 0), 0);
  const vencidos = encargos.filter(
    (e) => e.vencimiento && new Date(e.vencimiento) < new Date(),
  );

  const puedeAprobar = ['mando_medio', 'director'].includes(usuario.rol);
  // Contabilidad no aprueba, pero sí revisa. Su panel mostraba ceros
  // porque contaba lo suyo, y ella no rinde gastos: los revisa.
  const esContabilidad = usuario.rol === 'contabilidad';
  const porContabilizar = de('aprobado');

  return (
    <>
      <Titulo sub={`Hola, ${usuario.nombre.split(' ')[0]}`}>Panel</Titulo>

      {vencidos.length > 0 && (
        <Aviso tipo="bad">
          Tienes {vencidos.length} encargo{vencidos.length > 1 ? 's' : ''} vencido
          {vencidos.length > 1 ? 's' : ''}. Liquídalo cuanto antes.
        </Aviso>
      )}

      <div style={{
        display: 'grid', gap: 14, marginBottom: 22,
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      }}>
        {esContabilidad && (
          <Metrica
            etiqueta="Por contabilizar"
            valor={String(porContabilizar.cantidad)}
            detalle={fmt.money(porContabilizar.total)}
            alTocar={() => irA('revision')}
          />
        )}

        <Metrica etiqueta="Saldo por rendir" valor={fmt.money(saldoAbierto)}
                 detalle={`${encargos.length} encargo(s) abierto(s)`} />
        <Metrica etiqueta="Observados" valor={observados.cantidad}
                 detalle="Requieren corrección"
                 color={observados.cantidad ? T.warn : undefined} />
        {puedeAprobar && (
          <Metrica etiqueta="Por aprobar" valor={porAprobar.cantidad}
                   detalle={fmt.money(porAprobar.total)}
                   color={porAprobar.cantidad ? T.info : undefined} />
        )}
        <Metrica etiqueta="En SAP" valor={de('contabilizado').cantidad}
                 detalle="Ya contabilizados" />
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Boton variante="acento" onClick={() => irA('capturar')}>Capturar gasto</Boton>
        <Boton variante="suave" onClick={() => irA('mis-gastos')}>Mis gastos</Boton>
        {puedeAprobar && porAprobar.cantidad > 0 && (
          <Boton variante="suave" onClick={() => irA('aprobar')}>
            Aprobar ({porAprobar.cantidad})
          </Boton>
        )}
      </div>

      {encargos.length > 0 && (
        <>
          <h3 style={{ fontFamily: serif, fontSize: 18, color: T.navy, margin: '30px 0 12px' }}>
            Tus encargos abiertos
          </h3>
          <div style={{ display: 'grid', gap: 10 }}>
            {encargos.map((e) => {
              const vencido = e.vencimiento && new Date(e.vencimiento) < new Date();
              return (
                <Card key={e.id} style={{ padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{e.id}</div>
                      <div style={{ color: T.muted, fontSize: 12, marginTop: 2 }}>
                        {e.glosa || e.tipo}{e.ovId ? ` · ${e.ovId}` : ''}
                      </div>
                      {e.vencimiento && (
                        <div style={{ fontSize: 11, marginTop: 4, color: vencido ? T.bad : T.muted }}>
                          {vencido ? 'Venció' : 'Vence'} el {fmt.fecha(e.vencimiento)}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ fontFamily: serif, fontSize: 22, fontWeight: 600, color: T.navy }}>
                        {fmt.money(e.saldo, e.moneda)}
                      </div>
                      <div style={{ fontSize: 11, color: T.muted }}>por rendir</div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

function Metrica({ etiqueta, valor, detalle, color, alTocar }) {
  return (
    <Card onClick={alTocar} style={{ padding: 16, cursor: alTocar ? 'pointer' : 'default' }}>
      <div style={{
        fontSize: 11, color: T.muted, textTransform: 'uppercase',
        letterSpacing: '.05em', fontWeight: 600,
      }}>
        {etiqueta}
      </div>
      <div style={{
        fontFamily: serif, fontSize: 30, fontWeight: 600,
        color: color || T.navy, margin: '6px 0 2px',
      }}>
        {valor}
      </div>
      <div style={{ fontSize: 12, color: T.muted }}>{detalle}</div>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════
// CAPTURAR
// ════════════════════════════════════════════════════════════════════
// Las cuentas vienen del servidor, no escritas aquí. Antes estaban
// duplicadas en los dos lados y cambiar una lista dejaba la otra
// desfasada: el formulario ofrecía cuentas que ya no existían.
//
// Esta lista es solo el respaldo para cuando no hay señal.
/**
 * Convierte los reparos del servidor en algo que se entienda.
 * Antes se mostraba "Faltan datos obligatorios" sin decir cuál.
 */
const NOMBRES_CAMPO = {
  fecha: 'la fecha',
  total: 'el importe',
  moneda: 'la moneda',
  cuentaPcge: 'la cuenta contable',
  concepto: 'el concepto',
  proveedorRuc: 'el RUC del proveedor',
  proveedorRazon: 'la razón social',
  comprobanteSerie: 'la serie del comprobante',
  comprobanteCorrelativo: 'el número del comprobante',
  numeroDocumento: 'el número del documento',
  reciboSerie: 'la serie del recibo',
  ovId: 'la orden de venta',
  motivoSinOv: 'el motivo sin OV',
  medioPago: 'el medio de pago',
  lineas: 'los desplazamientos',
  motivo: 'el motivo del desplazamiento',
  importe: 'el importe del desplazamiento',
  origen: 'el origen',
  destino: 'el destino',
};

function explicarCampos(reparos) {
  const vistos = new Set();
  const partes = [];

  for (const r of reparos) {
    // La ruta puede venir como ['lineas', 0, 'motivo']
    const ruta = Array.isArray(r.path) ? r.path : [];
    const campo = [...ruta].reverse().find((x) => typeof x === 'string');
    const nombre = NOMBRES_CAMPO[campo] || campo || 'un dato';
    const enLinea = ruta[0] === 'lineas' && typeof ruta[1] === 'number'
      ? ` (desplazamiento ${ruta[1] + 1})`
      : '';
    const clave = nombre + enLinea;
    if (vistos.has(clave)) continue;
    vistos.add(clave);
    partes.push(clave);
  }

  if (partes.length === 0) return 'Faltan datos obligatorios o hay un valor inválido.';
  if (partes.length === 1) return `Revisa ${partes[0]}: falta o tiene un valor inválido.`;
  return `Revisa estos datos: ${partes.join(', ')}.`;
}

/** Convierte un archivo a base64 sin alterarlo. Para PDF. */
function comoBase64(archivo) {
  return new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onload = () => resolver(String(lector.result).split(',')[1]);
    lector.onerror = () => rechazar(new Error('No se pudo leer el archivo'));
    lector.readAsDataURL(archivo);
  });
}

const CUENTAS_RESPALDO = [
  ['631501', 'Movilidad'],
  ['631401', 'Alimentación'],
  ['631504', 'Combustible'],
  ['631503', 'Peaje y otros gastos'],
  ['656109', 'Suministros diversos · copias'],
];

/** Devuelve las cuentas del servidor, o el respaldo si aún no llegaron. */
function cuentasDe(catalogos) {
  const c = catalogos?.cuentas || catalogos?.cuentasPcge;
  if (Array.isArray(c) && c.length > 0) {
    return c.map((x) => [x.valor ?? x[0], x.texto ?? x[1]]);
  }
  return CUENTAS_RESPALDO;
}

export function Capturar({ usuario, enLinea, irA, onCapturado }) {
  const [ovs, setOvs] = useState([]);
  const [encargos, setEncargos] = useState([]);
  const [padron, setPadron] = useState([]);
  const [form, setForm] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    moneda: 'PEN', cuentaPcge: '631503', comprobanteTipo: 'factura',
    proveedorRuc: '', proveedorRazon: '', comprobanteSerie: '',
    comprobanteCorrelativo: '', subtotal: '', igv: '', total: '',
    // Recargo al consumo y servicios inafectos: parte del total que
    // no paga IGV y va a su propia cuenta.
    exonerado: '', cuentaExonerado: '639306',
    // Moneda extranjera: el libro de compras va en soles, asi que hay
    // que guardar con que tipo de cambio se convirtio.
    tipoCambio: '', tipoCambioFecha: '', tipoCambioFuente: '',
    concepto: '', ovId: '', encargoLiquidadoId: '', medioPago: 'transferencia',
    numeroDocumento: '', motivoSinOv: '', ocId: '', qrCrudo: '', dni: '',
    // Quien consumio el gasto, si no fue quien lo captura.
    consumidorId: null, consumidorNombre: '',
  });
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const [tipoDoc, setTipoDoc] = useState('comprobante');
  const [catalogos, setCatalogos] = useState(null);
  const [lineasMovilidad, setLineasMovilidad] = useState([]);
  const [abrirQr, setAbrirQr] = useState(false);
  const [leyendoFoto, setLeyendoFoto] = useState(false);
  const [resultadoFoto, setResultadoFoto] = useState(null);
  const [fechaCambiada, setFechaCambiada] = useState(null);
  const [repetido, setRepetido] = useState(null);
  const fotoRef = React.useRef(null);
  const [avisoQr, setAvisoQr] = useState(null);
  const [adjunto, setAdjunto] = useState(null);

  useEffect(() => {
    (async () => {
      // Cada carga por separado.
      //
      // Antes iban juntas con Promise.all: si UNA fallaba, se perdían
      // las cuatro y en silencio. Por eso el padrón salía vacío sin
      // que apareciera ningún error, aunque el problema estuviera en
      // otra de las consultas.
      const [o, e, c, pr] = await Promise.allSettled([
        api.ovs(true), api.encargos(true), api.catalogos(), api.personal(),
      ]);

      if (o.status === 'fulfilled') setOvs(o.value.ovs || []);
      if (e.status === 'fulfilled') setEncargos(e.value.encargos || []);
      if (c.status === 'fulfilled') setCatalogos(c.value);
      if (pr.status === 'fulfilled') setPadron(pr.value.personal || []);

      // Lo que no cargó se dice, en lugar de dejar un campo vacío que
      // parece que la empresa no tiene personal registrado.
      const fallaron = [
        [o, 'las órdenes de venta'],
        [e, 'los encargos'],
        [c, 'las cuentas contables'],
        [pr, 'el padrón de personal'],
      ].filter(([r]) => r.status === 'rejected');

      if (fallaron.length > 0) {
        const detalle = fallaron[0][0].reason?.detalle?.detalle;
        setAvisoQr({
          tipo: 'warn',
          texto: `No se pudo cargar ${fallaron.map(([, n]) => n).join(' ni ')}. `
               + (detalle || 'Puedes capturar igual, escribiendo los datos a mano.'),
        });
      }
    })();
  }, []);

  // El QR trae RUC, serie, número, IGV, total y fecha. La razón social
  // y el concepto no vienen: esos se completan aparte.
  /**
   * Lee el comprobante de una foto y llena el formulario.
   * Lo que ya está escrito no se pisa: si el técnico tecleó algo, gana
   * lo suyo sobre lo que leyó la máquina.
   */
  const leerDeFoto = (e) => {
    const archivo = e.target.files?.[0];
    e.target.value = '';
    if (archivo) leerDocumento(archivo);
  };

  const leerDocumento = async (archivo) => {
    if (!archivo) return;
    setLeyendoFoto(true);
    setResultadoFoto(null);
    try {
      // Un PDF se manda tal cual: comprimirlo como foto lo destruiría.
      // Además se lee mejor, porque el texto ya está dentro del archivo
      // en vez de haber que reconocerlo de una imagen.
      const esPdf = archivo.type === 'application/pdf'
        || /\.pdf$/i.test(archivo.name || '');

      const f = esPdf
        ? {
            base64: await comoBase64(archivo),
            tipoMime: 'application/pdf',
            nombre: archivo.name || 'comprobante.pdf',
            bytes: archivo.size,
          }
        : await prepararFoto(archivo);

      const r = await api.leerFoto(f.base64, f.tipoMime);

      setForm((x) => {
        const nuevo = { ...x };

        for (const [k, v] of Object.entries(r.campos || {})) {
          if (k === 'tipoLeido') continue;
          if (v === null || v === undefined) continue;

          // Estos campos vienen del PAPEL y mandan sobre lo que haya en
          // el formulario. La fecha arranca con el día de hoy, así que
          // nunca estaba vacía y la lectura la saltaba: el gasto se
          // registraba con la fecha de captura, no la del comprobante.
          const mandaElPapel = [
            'fecha', 'total', 'subtotal', 'igv', 'exonerado', 'moneda',
            'comprobanteSerie', 'comprobanteCorrelativo', 'proveedorRuc',
          ].includes(k);

          if (mandaElPapel || !nuevo[k]) nuevo[k] = String(v);
        }

        // El desglose leído del papel no se recalcula después
        if (r.campos?.igv != null) nuevo.desgloseLeido = true;

        return nuevo;
      });

      // Si la fecha del documento no es la de hoy, conviene decirlo:
      // una factura de la semana pasada capturada hoy es normal, pero
      // el usuario tiene que ver con qué fecha va a quedar.
      const fechaLeida = r.campos?.fecha;
      const hoy = new Date().toISOString().slice(0, 10);
      if (fechaLeida && fechaLeida !== hoy) setFechaCambiada(fechaLeida);

      setResultadoFoto(r);

      // Si el QR había fallado, la foto es la única fuente: conviene
      // decirlo, porque exige más cuidado al revisar.
      setAvisoQr(r.completitud >= 80
        ? { tipo: 'ok', texto: 'Datos leídos de la foto. Contrástalos con el papel antes de enviar.' }
        : { tipo: 'warn', texto: `Solo se pudo leer el ${r.completitud}% del documento. Completa el resto a mano.` });

      // La foto queda guardada para adjuntarla al gasto al enviarlo:
      // así el técnico no tiene que fotografiar el mismo papel dos veces.
      setAdjunto({
        nombre: f.nombre, mimeType: f.tipoMime,
        bytes: f.bytes, contenidoBase64: f.base64,
      });
    } catch (err) {
      setAvisoQr({
        tipo: 'bad',
        texto: err.detalle?.detalle
          || 'No se pudo leer la foto. Llena los campos a mano.',
      });
    }
    setLeyendoFoto(false);
  };

  const procesarQr = async (texto) => {
    setAbrirQr(false);
    try {
      const r = await api.leerQr(texto);
      const d = r.datos;
      setForm((f) => ({
        ...f,
        proveedorRuc: d.proveedorRuc || f.proveedorRuc,
        comprobanteTipo: d.comprobanteTipo || f.comprobanteTipo,
        comprobanteSerie: d.comprobanteSerie || f.comprobanteSerie,
        comprobanteCorrelativo: d.comprobanteCorrelativo || f.comprobanteCorrelativo,
        subtotal: d.subtotal ?? f.subtotal,
        igv: d.igv ?? f.igv,
        exonerado: d.exonerado ?? f.exonerado,
        total: d.total ?? f.total,
        // El QR trae el IGV exacto que declaró el emisor. A partir de
        // aquí no se recalcula: lo que dice el papel es lo que va.
        desgloseLeido: d.igv != null || f.desgloseLeido,
        // La fecha del comprobante manda sobre la de captura
        fecha: d.fecha || f.fecha,
        // El QR no trae estos dos, pero el servidor los completa desde
        // el registro de proveedores y desde la última vez que se
        // compró a ese mismo proveedor.
        proveedorRazon: d.proveedorRazon || f.proveedorRazon,
        concepto: f.concepto || d.concepto || '',
        // La cuenta que eligió la persona MANDA sobre la aprendida.
        //
        // Antes era al revés: quien marcaba "Combustible" y luego
        // escaneaba el QR, veía cómo la cuenta de la última compra a
        // ese proveedor le pisaba la elección. Peajes y combustible
        // terminaron contabilizados como alimentación por esto.
        cuentaPcge: f.cuentaTocada ? f.cuentaPcge : (d.cuentaPcge || f.cuentaPcge),
        qrCrudo: r.crudo,
      }));

      const hoy = new Date().toISOString().slice(0, 10);
      if (d.fecha && d.fecha !== hoy) setFechaCambiada(d.fecha);

      if (r.yaCapturado) {
        setAvisoQr({
          tipo: 'bad',
          texto: `Ese comprobante ya fue capturado (${r.yaCapturado.id}, ${r.yaCapturado.estado}). `
               + 'Revisa antes de continuar.',
        });
      } else if (r.aprendido?.concepto) {
        // Se dice cuánto respaldo tiene la sugerencia. Una vez sola no
        // es lo mismo que ocho: quien captura decide si confía.
        const a = r.aprendido;
        const respaldo = a.compras > 1
          ? `es lo que se puso en ${a.vecesConEseConcepto} de las ${a.compras} compras a este proveedor`
          : 'es lo que se puso la única vez anterior';
        setAvisoQr({
          tipo: 'ok',
          texto: `Datos leídos del QR. Se sugiere «${a.concepto}»: ${respaldo}. `
               + 'Cámbialo si esta compra fue de otra cosa.',
        });
      } else if (d.proveedorRazon) {
        setAvisoQr({
          tipo: 'ok',
          texto: `Datos leídos del QR. Falta el concepto: escribe qué se compró.`,
        });
      } else {
        setAvisoQr({
          tipo: 'ok',
          texto: 'Datos leídos del QR. Falta la razón social y el concepto. '
               + 'La próxima vez que compres a este proveedor se llenarán solos.',
        });
      }
    } catch (err) {
      setAvisoQr({
        tipo: 'bad',
        texto: err.detalle?.detalle || 'No se pudo interpretar el código. Llena los datos a mano.',
      });
    }
  };

  const elegirArchivo = async (e) => {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    if (archivo.size > 6 * 1024 * 1024) {
      return setError('La imagen pesa más de 6 MB. Toma la foto en menor resolución.');
    }
    try { setAdjunto(await leerArchivo(archivo)); }
    catch { setError('No se pudo leer el archivo.'); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Si subtotal + IGV no da el total, el asiento va a salir descuadrado.
  // Vale la pena decirlo ANTES de enviar, no cuando contabilidad lo
  // devuelva.
  const desgloseNoCuadra = (() => {
    const sub = parseFloat(form.subtotal);
    const ig = parseFloat(form.igv);
    const exo = parseFloat(form.exonerado) || 0;
    const tot = parseFloat(form.total);
    if (!(sub > 0) || !(tot > 0) || Number.isNaN(ig)) return null;
    const suma = Math.round((sub + ig + exo) * 100) / 100;
    if (Math.abs(suma - tot) <= 0.05) return null;
    return `${sub.toFixed(2)} + ${ig.toFixed(2)} IGV`
         + (exo > 0 ? ` + ${exo.toFixed(2)} exonerado` : '')
         + ` da ${suma.toFixed(2)}, pero el total dice ${tot.toFixed(2)}.`;
  })();

  // ── La trampa que dejó pasar tres facturas mal ────────────────────
  //
  // El control de arriba solo mira que las partes sumen el total. Una
  // factura de 200 con 183 de base y 17 de IGV suma exacto... y es
  // imposible: 17 no es el 18% de 183, es el 9.3%.
  //
  // Cuando la tasa no es 18% y no hay nada declarado como exonerado,
  // o la factura trae recargo al consumo sin separar, o una de las dos
  // cifras no es la del papel. En ambos casos hay que mirar antes de
  // enviar, porque después el asiento viaja mal al SAP.
  const tasaIgv = (() => {
    const sub = parseFloat(form.subtotal);
    const ig = parseFloat(form.igv);
    if (!(sub > 0) || !(ig > 0)) return null;
    return (ig / sub) * 100;
  })();
  const tasaRara = tipoDoc === 'comprobante'
    && !(parseFloat(form.exonerado) > 0)
    && tasaIgv != null
    && Math.abs(tasaIgv - 18) > 0.6;

  // ── Moneda y tipo de cambio ───────────────────────────────────────
  //
  // Una factura en dólares no se asienta en dólares: el libro de
  // compras va en soles, al tipo de cambio venta de la fecha de
  // emisión. Sin ese dato el gasto no se puede contabilizar, así que
  // se resuelve aquí y no tres semanas después.
  const monedas = catalogos?.monedas
    || [{ codigo: 'PEN', nombre: 'Soles' }, { codigo: 'USD', nombre: 'Dólares' }];
  const esExtranjera = form.moneda !== 'PEN';
  const [buscandoTc, setBuscandoTc] = useState(false);
  const [tcAMano, setTcAMano] = useState(false);

  /** Pregunta el tipo de cambio de una fecha; si no hay, lo pide a mano. */
  const traerTipoCambio = async (moneda, fecha) => {
    if (!moneda || moneda === 'PEN' || !fecha) return;
    setBuscandoTc(true);
    setTcAMano(false);
    try {
      const r = await api.tipoCambio(fecha, moneda);
      if (r?.tipoCambio > 0) {
        setForm((f) => ({
          ...f,
          tipoCambio: String(r.tipoCambio),
          tipoCambioFecha: r.fecha || fecha,
          tipoCambioFuente: r.fuente || 'SUNAT',
        }));
      } else {
        // No hay publicación o el servicio no respondió: se pide a
        // mano en lugar de inventar una cifra.
        setTcAMano(true);
        setForm((f) => ({ ...f, tipoCambioFuente: 'Escrito a mano' }));
      }
    } catch {
      setTcAMano(true);
      setForm((f) => ({ ...f, tipoCambioFuente: 'Escrito a mano' }));
    } finally {
      setBuscandoTc(false);
    }
  };

  const setMoneda = (codigo) => {
    setForm((f) => (codigo === 'PEN'
      // Volver a soles limpia el tipo de cambio: dejarlo puesto haría
      // que un gasto en soles viajara con un tipo de cambio colgado.
      ? { ...f, moneda: codigo, tipoCambio: '', tipoCambioFecha: '', tipoCambioFuente: '' }
      : { ...f, moneda: codigo }));
    if (codigo !== 'PEN') traerTipoCambio(codigo, form.fecha);
  };

  // Si cambia la moneda o la fecha, el tipo de cambio que había ya no
  // corresponde: se vuelve a preguntar.
  useEffect(() => {
    if (esExtranjera && form.fecha) traerTipoCambio(form.moneda, form.fecha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.moneda, form.fecha]);

  const tcNumero = parseFloat(form.tipoCambio) || 0;
  const totalEnSoles = esExtranjera && tcNumero > 0
    ? (parseFloat(form.total) || 0) * tcNumero
    : null;
  // Sin tipo de cambio el servidor va a rechazar la captura: mejor
  // decirlo antes de que pulse Enviar.
  const faltaTipoCambio = esExtranjera && !(tcNumero > 0);

  // Despeja la parte exonerada a partir del IGV, que es la cifra que el
  // emisor declaró y no se puede inventar: base = IGV / 0.18, y lo que
  // sobra del total es lo que no paga IGV.
  const separarExonerado = () => {
    const ig = parseFloat(form.igv) || 0;
    const tot = parseFloat(form.total) || 0;
    if (ig <= 0 || tot <= 0) return;
    const gravado = ig / 0.18;
    const resto = Math.round((tot - gravado - ig) * 100) / 100;
    if (resto < -0.05) {
      setError('Con ese IGV la base gravada ya supera el total. '
             + 'Revisa las cifras contra la factura.');
      return;
    }
    setForm((f) => ({
      ...f,
      subtotal: gravado.toFixed(2),
      exonerado: Math.max(0, resto).toFixed(2),
      cuentaExonerado: f.cuentaExonerado || '639306',
      desgloseLeido: true,
    }));
  };

  // El IGV se sugiere al 18%, pero NUNCA pisa lo que vino del
  // comprobante.
  //
  // Una boleta de restaurante lleva recargo al consumo, que no paga
  // IGV. Una con bolsas lleva ICBPER. En esos casos el 18% del total
  // no es el IGV real, y recalcularlo hacía que el asiento no cuadrara
  // con la factura. Si el QR o la foto trajeron el desglose, ese manda.
  const setSubtotal = (v) => {
    const s = parseFloat(v) || 0;
    setForm((f) => {
      if (f.desgloseLeido) {
        // Vino del comprobante: se cambia solo lo que el usuario tocó
        return { ...f, subtotal: v };
      }
      const igv = Math.round(s * 0.18 * 100) / 100;
      return { ...f, subtotal: v, igv: igv || '', total: (s + igv) || '' };
    });
  };

  /** El usuario corrige el IGV a mano: a partir de ahí manda él. */
  const setIgv = (v) => setForm((f) => ({
    ...f, igv: v, desgloseLeido: true,
    total: ((parseFloat(f.subtotal) || 0) + (parseFloat(v) || 0)).toFixed(2),
  }));

  // Encargos del usuario que cubren el gasto y corresponden a la misma OV.
  const encargosAplicables = encargos.filter(
    (e) => (!form.ovId || e.ovId === form.ovId) && e.saldo >= (parseFloat(form.total) || 0),
  );

  const enviar = async (e) => {
    e.preventDefault();

    const esMovilidad = tipoDoc === 'planilla_movilidad';
    const totalMovilidad = lineasMovilidad.reduce((s, l) => s + Number(l.importe || 0), 0);

    if (esMovilidad) {
      if (lineasMovilidad.length === 0) {
        return setError('Agrega al menos un desplazamiento con motivo e importe.');
      }
    } else if (!form.total || parseFloat(form.total) <= 0) {
      return setError('Indica el monto total.');
    }

    if (!form.ovId && !form.motivoSinOv) {
      return setError('Elige una orden de venta, o indica el motivo si no corresponde a una.');
    }

    setError(''); setOcupado(true);

    const gasto = {
      tipoDocumento: tipoDoc,
      numeroDocumento: form.numeroDocumento || null,
      consumidorId: form.consumidorId || null,
      consumidorNombre: form.consumidorNombre || null,
      motivoSinOv: form.ovId ? null : (form.motivoSinOv || null),
      ocId: form.ocId || null,
      qrCrudo: form.qrCrudo || null,
      ...(esMovilidad ? { lineas: lineasMovilidad } : {}),
      fecha: esMovilidad
        ? lineasMovilidad.map((l) => l.fecha).sort()[0]
        : form.fecha,
      total: esMovilidad ? totalMovilidad : parseFloat(form.total),
      // La movilidad y los sustentos internos no llevan IGV.
      subtotal: tipoDoc === 'comprobante' ? (parseFloat(form.subtotal) || undefined) : undefined,
      igv: tipoDoc === 'comprobante' ? (parseFloat(form.igv) || undefined) : undefined,
      // Si hay parte exonerada, al SAP viajan dos lineas de asiento.
      exonerado: tipoDoc === 'comprobante' && parseFloat(form.exonerado) > 0
        ? parseFloat(form.exonerado) : undefined,
      cuentaExonerado: tipoDoc === 'comprobante' && parseFloat(form.exonerado) > 0
        ? (form.cuentaExonerado || '639306') : undefined,
      moneda: form.moneda,
      ...(form.moneda !== 'PEN' && parseFloat(form.tipoCambio) > 0 ? {
        tipoCambio: parseFloat(form.tipoCambio),
        tipoCambioFecha: form.tipoCambioFecha || undefined,
        tipoCambioFuente: form.tipoCambioFuente || undefined,
      } : {}),
      concepto: form.concepto || undefined,
      cuentaPcge: esMovilidad ? '631501' : form.cuentaPcge,
      proveedorRuc: form.proveedorRuc || null,
      proveedorRazon: form.proveedorRazon || null,
      comprobanteTipo: form.comprobanteTipo || null,
      comprobanteSerie: form.comprobanteSerie || null,
      comprobanteCorrelativo: form.comprobanteCorrelativo || null,
      medioPago: form.medioPago,
      ovId: form.ovId || null,
      encargoLiquidadoId: form.encargoLiquidadoId || null,
    };

    try {
      if (!enLinea) {
        colaOffline.agregar({ payload: gasto, adjunto });
        onCapturado({ sinSenal: true });
      } else {
        const r = await api.crearGasto(gasto);
        // La foto se sube después: primero tiene que existir el gasto.
        if (adjunto && r.gasto?.id) {
          try {
            await api.subirAdjunto(r.gasto.id, {
              ...adjunto,
              tipo: esMovilidad ? 'planilla_firmada' : 'foto_comprobante',
            });
          } catch { /* el gasto ya quedó guardado; la foto se puede subir luego */ }
        }
        // El número de serie que le asignó la plataforma: quien captura
        // tiene que verlo para poder anotarlo o buscarlo después.
        onCapturado({
          sinSenal: false,
          numeroAsignado: r.numeroAsignado,
          avisoNumeracion: r.avisoNumeracion,
        });
      }
      irA('mis-gastos');
    } catch (err) {
      const d = err.detalle;
      if (d?.error === 'documento_repetido') {
        // Se guarda aparte para poder mostrar de quién es y en qué
        // estado está, no solo un mensaje suelto.
        setRepetido(d.yaRendido || { detalle: d.detalle });
        setError('');
      } else if (d?.error === 'excede_tope_movilidad') {
        setError(Array.isArray(d.detalle) ? d.detalle.join(' ') : 'Excede el tope diario de movilidad.');
      } else if (d?.detalle && typeof d.detalle === 'string') {
        setError(d.detalle);
      } else if (Array.isArray(d?.detalle) && d.detalle.length > 0) {
        // El servidor dice exactamente qué campo falla, pero antes se
        // descartaba y salía un mensaje genérico imposible de
        // diagnosticar. Ahora se traduce a algo accionable.
        setError(explicarCampos(d.detalle));
      } else if (d?.error === 'error_interno') {
        setError(d.detalle
          ? `Falló el servidor. ${d.detalle}`
          : 'Falló el servidor. Vuelve a intentar; si sigue igual, avisa a Hernán.');
      } else if (d?.error) {
        // Nunca más un mensaje mudo: si hay código, se muestra. Poder
        // nombrar el fallo es lo que permite corregirlo.
        setError(`No se pudo guardar (${d.error}). Revisa los datos e inténtalo otra vez.`);
      } else {
        setError('Faltan datos obligatorios o hay un valor inválido.');
      }
      setOcupado(false);
    }
  };

  const esMovilidad = tipoDoc === 'planilla_movilidad';
  const esRecibo = tipoDoc === 'recibo_ad';
  const esComprobante = tipoDoc === 'comprobante';
  const tope = catalogos?.topeMovilidadDiario ?? 45.2;

  return (
    <>
      {abrirQr && (
        <LectorQr
          onLeido={procesarQr}
          onCerrar={() => setAbrirQr(false)}
          lecturaFotoDisponible
          onLeerDocumento={(archivo) => { setAbrirQr(false); leerDocumento(archivo); }}
        />
      )}

      <Titulo sub="Elige con qué se sustenta el gasto">Capturar gasto</Titulo>

      {!enLinea && (
        <Aviso tipo="warn">
          Sin señal. Puedes registrar el gasto igual: se guarda en tu
          teléfono y sube solo cuando recuperes conexión.
        </Aviso>
      )}
      {error && <Aviso tipo="bad">{error}</Aviso>}

      {/* Tipo de sustento */}
      <div style={{ display: 'grid', gap: 8, marginBottom: 18 }}>
        {(catalogos?.tiposDocumento || []).map((t) => (
          <Card key={t.valor} onClick={() => { setTipoDoc(t.valor); setAvisoQr(null); }}
                style={{
                  padding: 13,
                  borderColor: tipoDoc === t.valor ? T.navy : T.border,
                  borderWidth: tipoDoc === t.valor ? 2 : 1,
                  background: tipoDoc === t.valor ? T.warm : T.paper,
                }}>
            <div style={{
              fontWeight: 600, fontSize: 14,
              color: tipoDoc === t.valor ? T.navy : T.text,
            }}>
              {t.texto}
            </div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{t.ayuda}</div>
          </Card>
        ))}
      </div>

      {repetido && (
        <div style={{
          background: T.maloBg, borderLeft: `3px solid ${T.bad}`,
          borderRadius: 9, padding: 15, marginBottom: 16,
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: T.bad,
            letterSpacing: '.05em', marginBottom: 7,
          }}>
            ESTE DOCUMENTO YA SE RINDIÓ
          </div>

          {repetido.id ? (
            <>
              <div style={{ fontSize: 14, marginBottom: 6 }}>
                Está registrado como <strong>{repetido.id}</strong>
                {repetido.estado === 'contabilizado' && ' y ya está contabilizado en SAP'}
                {repetido.estado === 'aprobado' && ' y ya fue aprobado'}
                {repetido.estado === 'enviado' && ' y está esperando aprobación'}
                {repetido.estado === 'observado' && ' y está observado, esperando corrección'}
                .
              </div>
              <div style={{ fontSize: 12, color: T.tinta }}>
                {fmt.fecha(repetido.fecha)} · {fmt.money(repetido.total, repetido.moneda)}
                {repetido.autor && ` · lo rindió ${repetido.autor}`}
              </div>
              {repetido.esDeOtro && (
                <div style={{ fontSize: 12, color: T.bad, marginTop: 8 }}>
                  Lo rindió otra persona. Si fueron los dos al mismo proveedor,
                  pónganse de acuerdo sobre quién lo sustenta.
                </div>
              )}
              {repetido.estado === 'observado' && (
                <div style={{ fontSize: 12, color: T.muted, marginTop: 8 }}>
                  Si lo que quieres es corregirlo, búscalo en Mis gastos en lugar
                  de capturarlo de nuevo.
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 14 }}>{repetido.detalle}</div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Boton type="button" variante="suave"
                   onClick={() => irA('mis-gastos')}
                   style={{ fontSize: 12, padding: '6px 12px' }}>
              Ver mis gastos
            </Boton>
            <Boton type="button" variante="suave"
                   onClick={() => setRepetido(null)}
                   style={{ fontSize: 12, padding: '6px 12px' }}>
              Cerrar
            </Boton>
          </div>
        </div>
      )}

      <form onSubmit={enviar}>
        {/* ── Comprobante ── */}
        {esComprobante && (
          <>
            <Boton type="button" variante="acento" onClick={() => setAbrirQr(true)}
                   style={{ width: '100%', marginBottom: 8, padding: 13 }}>
              Escanear el QR del comprobante
            </Boton>

            {/* La foto complementa al QR: sirve para la razón social y
                el concepto, y para las boletas que no tienen QR. */}
            {/* Antes este botón se escondía cuando faltaba la clave de
                lectura, así que parecía que la función no existía. Ahora
                siempre se ve y explica qué falta. */}
            <Boton type="button" variante="suave"
                   onClick={() => {
                     if (catalogos?.lecturaFotoDisponible) fotoRef.current?.click();
                     else setAvisoQr({
                       tipo: 'warn',
                       texto: 'La lectura automática no está activada. Falta la variable '
                            + 'OCR_API_KEY en el panel de Hostinger. Mientras tanto, escanea '
                            + 'el QR o llena los campos a mano.',
                     });
                   }}
                   disabled={leyendoFoto}
                   style={{ width: '100%', marginBottom: 12, padding: 12 }}>
              {leyendoFoto
                ? 'Leyendo la foto…'
                : 'Foto o PDF del comprobante, y llena todo automáticamente'}
            </Boton>
            {/* PDF además de foto: las facturas electrónicas llegan por
                correo en PDF, y ese caso se lee mejor que una foto. */}
            <input ref={fotoRef} type="file" accept="image/*,application/pdf"
                   onChange={leerDeFoto} style={{ display: 'none' }} />

            {fechaCambiada && (
              <div style={{
                background: T.infoBg, borderLeft: `3px solid ${T.info}`,
                borderRadius: '0 8px 8px 0', padding: '10px 14px',
                marginBottom: 12, fontSize: 12.5, color: T.info,
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', gap: 10,
              }}>
                <span>
                  El gasto queda con la fecha del comprobante:{' '}
                  <strong>{fmt.fecha(fechaCambiada)}</strong>, no la de hoy.
                </span>
                <button onClick={() => setFechaCambiada(null)} style={{
                  background: 'none', border: 'none', color: T.info,
                  fontSize: 16, cursor: 'pointer', padding: 0, lineHeight: 1,
                }}>×</button>
              </div>
            )}

            {resultadoFoto && (
              <div style={{
                background: T.warm, border: `1px solid ${T.border}`,
                borderRadius: 9, padding: 13, marginBottom: 14,
              }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: 8, gap: 10,
                }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.navy }}>
                    Leído de la foto
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 9,
                    background: resultadoFoto.completitud >= 80 ? T.okBg : T.warnBg,
                    color: resultadoFoto.completitud >= 80 ? T.ok : T.warn,
                  }}>
                    {resultadoFoto.completitud}% COMPLETO
                  </span>
                </div>

                <div style={{ fontSize: 12, color: T.tinta, marginBottom: 8 }}>
                  Los campos se llenaron solos. <strong>Contrástalos con el papel
                  antes de enviar</strong>: una lectura equivocada que nadie revisa
                  es peor que teclear.
                </div>

                {resultadoFoto.avisos?.map((a, i) => (
                  <div key={i} style={{
                    fontSize: 12, marginTop: 6,
                    color: a.calculado ? T.muted : T.bad,
                  }}>
                    · {a.texto}
                  </div>
                ))}

                {resultadoFoto.dudosos?.length > 0 && (
                  <div style={{ fontSize: 12, color: T.warn, marginTop: 8 }}>
                    Revisa con cuidado: {resultadoFoto.dudosos.join(', ')}
                  </div>
                )}
              </div>
            )}
            {avisoQr && <Aviso tipo={avisoQr.tipo}>{avisoQr.texto}</Aviso>}

            <Card style={{ marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 14 }}>
                <Campo etiqueta="Fecha">
                  <input type="date" value={form.fecha}
                         onChange={(e) => set('fecha', e.target.value)} style={estiloInput} />
                </Campo>
                <Campo etiqueta="Tipo">
                  <select value={form.comprobanteTipo}
                          onChange={(e) => set('comprobanteTipo', e.target.value)}
                          style={estiloInput}>
                    <option value="factura">Factura</option>
                    <option value="boleta">Boleta</option>
                    <option value="recibo_honorarios">Recibo por honorarios</option>
                    <option value="ticket">Ticket</option>
                  </select>
                </Campo>
              </div>

              <Campo etiqueta="RUC del proveedor">
                <input value={form.proveedorRuc} inputMode="numeric" maxLength={11}
                       onChange={(e) => set('proveedorRuc', e.target.value.replace(/\D/g, ''))}
                       placeholder="20512345678" style={estiloInput} />
              </Campo>
              <Campo etiqueta="Razón social" ayuda="El QR no la trae: escríbela.">
                <input value={form.proveedorRazon}
                       onChange={(e) => set('proveedorRazon', e.target.value)}
                       style={estiloInput} />
              </Campo>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 14 }}>
                <Campo etiqueta="Serie">
                  <input value={form.comprobanteSerie}
                         onChange={(e) => set('comprobanteSerie', e.target.value.toUpperCase())}
                         placeholder="F001" style={estiloInput} />
                </Campo>
                <Campo etiqueta="Número">
                  <input value={form.comprobanteCorrelativo}
                         onChange={(e) => set('comprobanteCorrelativo', e.target.value)}
                         placeholder="0004521" style={estiloInput} />
                </Campo>
              </div>

              {/* ── Moneda ──────────────────────────────────────────
                  Antes estaba fija en soles dentro del código, sin
                  manera de elegirla: una factura en dólares se
                  registraba como si fueran soles y el gasto quedaba
                  subvaluado casi cuatro veces. */}
              <Campo
                etiqueta="Moneda"
                ayuda={form.moneda === 'PEN'
                  ? 'La del documento, no la de quien paga.'
                  : 'Se convierte a soles para el libro de compras.'}
              >
                <select value={form.moneda}
                        onChange={(e) => setMoneda(e.target.value)}
                        style={estiloInput}>
                  {monedas.map((m) => (
                    <option key={m.codigo} value={m.codigo}>
                      {m.codigo} · {m.nombre}
                    </option>
                  ))}
                </select>
              </Campo>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', columnGap: 12 }}>
                <Campo etiqueta="Subtotal">
                  <input inputMode="decimal" value={form.subtotal}
                         onChange={(e) => setSubtotal(e.target.value)} style={estiloInput} />
                </Campo>
                <Campo
                  etiqueta="IGV"
                  ayuda={form.desgloseLeido
                    ? 'Tomado del comprobante. No se recalcula.'
                    : 'Se sugiere el 18%. Corrígelo si la factura dice otra cosa.'}
                >
                  <input inputMode="decimal" value={form.igv}
                         onChange={(e) => setIgv(e.target.value)} style={estiloInput} />
                </Campo>
                <Campo etiqueta="Total">
                  <input inputMode="decimal" value={form.total}
                         onChange={(e) => set('total', e.target.value)}
                         style={{ ...estiloInput, fontWeight: 700 }} />
                </Campo>
              </div>

              {/* ── Tipo de cambio ──────────────────────────────────
                  Solo aparece cuando la moneda no es soles. El importe
                  en soles no es decorativo: es el que entra al libro
                  de compras y el que sustenta el crédito fiscal. */}
              {esExtranjera && (
                <div style={{
                  border: `1px solid ${faltaTipoCambio ? T.bad : T.navy}`,
                  borderRadius: 8, padding: 12, marginTop: 12,
                }}>
                  <div style={{
                    fontSize: 12.5, fontWeight: 600,
                    color: faltaTipoCambio ? T.bad : T.navy, marginBottom: 2,
                  }}>
                    Tipo de cambio
                  </div>
                  <div style={{
                    fontSize: 11, color: T.muted,
                    marginBottom: 10, lineHeight: 1.4,
                  }}>
                    El venta de la SUNAT a la fecha de emisión, que es el que
                    exige la norma para el crédito fiscal. No el de hoy.
                  </div>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1.4fr',
                    columnGap: 12,
                  }}>
                    <Campo etiqueta={`1 ${form.moneda} = S/`}>
                      <input inputMode="decimal" value={form.tipoCambio}
                             placeholder={buscandoTc ? 'Consultando…' : '0.0000'}
                             onChange={(e) => {
                               set('tipoCambio', e.target.value);
                               setForm((f) => ({
                                 ...f, tipoCambioFuente: 'Escrito a mano',
                               }));
                             }}
                             style={estiloInput} />
                    </Campo>
                    <Campo etiqueta="Equivale a">
                      <div style={{
                        ...estiloInput,
                        display: 'flex', alignItems: 'center',
                        background: T.okBg, fontWeight: 700,
                        color: totalEnSoles ? T.navy : T.muted,
                      }}>
                        {totalEnSoles
                          ? fmt.money(totalEnSoles, 'PEN')
                          : '—'}
                      </div>
                    </Campo>
                  </div>
                  {form.tipoCambioFuente && !faltaTipoCambio && (
                    <div style={{ fontSize: 11, color: T.muted, marginTop: -4 }}>
                      {form.tipoCambioFuente}
                    </div>
                  )}
                  {faltaTipoCambio && (
                    <div style={{ fontSize: 11.5, color: T.bad, marginTop: 4, lineHeight: 1.4 }}>
                      {tcAMano
                        ? 'No hay publicación oficial para esta moneda o fecha. '
                          + 'Escribe el tipo de cambio que usó el banco.'
                        : 'Hace falta para poder contabilizar el gasto.'}
                    </div>
                  )}
                </div>
              )}

              {/* ── Parte exonerada de IGV ──────────────────────────
                  Solo aparece cuando hace falta: si la lectura la trajo
                  o si la tasa de IGV delata que algo del total no está
                  gravado. No tiene sentido estorbar en las facturas
                  normales, que son la mayoría. */}
              {(parseFloat(form.exonerado) > 0 || tasaRara) && (
                <div style={{
                  border: `1px solid ${T.navy}`, borderRadius: 8,
                  padding: 12, marginTop: 12,
                }}>
                  <div style={{
                    fontSize: 12.5, fontWeight: 600,
                    color: T.navy, marginBottom: 2,
                  }}>
                    Parte exonerada de IGV
                  </div>
                  <div style={{
                    fontSize: 11, color: T.muted,
                    marginBottom: 10, lineHeight: 1.4,
                  }}>
                    Recargo al consumo y servicios inafectos. Se paga, pero
                    no lleva IGV: va a su propia cuenta en el asiento.
                  </div>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1.2fr',
                    columnGap: 12,
                  }}>
                    <Campo etiqueta="Importe">
                      <input inputMode="decimal" value={form.exonerado}
                             placeholder="0.00"
                             onChange={(e) => set('exonerado', e.target.value)}
                             style={estiloInput} />
                    </Campo>
                    <Campo etiqueta="Cuenta">
                      <input value={form.cuentaExonerado}
                             onChange={(e) => set('cuentaExonerado', e.target.value)}
                             disabled={!(parseFloat(form.exonerado) > 0)}
                             style={{
                               ...estiloInput,
                               opacity: parseFloat(form.exonerado) > 0 ? 1 : 0.5,
                             }} />
                    </Campo>
                  </div>
                </div>
              )}

              {tasaRara && (
                <Aviso tipo="warn">
                  El IGV es el {tasaIgv.toFixed(1)}% de la base, no el 18%.
                  Si la factura trae recargo al consumo, sepáralo; si no,
                  una de las dos cifras no es la del papel.
                  <div style={{ marginTop: 10 }}>
                    <Boton variante="suave" onClick={separarExonerado}
                           style={{ fontSize: 12.5 }}>
                      Separar: {(parseFloat(form.igv) / 0.18).toFixed(2)} gravado
                      {' + '}
                      {Math.max(0, (parseFloat(form.total) || 0)
                        - parseFloat(form.igv) / 0.18
                        - (parseFloat(form.igv) || 0)).toFixed(2)} exonerado
                    </Boton>
                  </div>
                </Aviso>
              )}

              {desgloseNoCuadra && (
                <Aviso tipo="warn">
                  El desglose no cuadra: {desgloseNoCuadra} Revísalo contra
                  la factura antes de enviar.
                </Aviso>
              )}
            </Card>
          </>
        )}

        {/* ── Planilla de movilidad ── */}
        {esMovilidad && (
          <>
            <Card style={{ marginBottom: 14 }}>
              {/* El número ya no se teclea: lo asigna la plataforma al
                  registrar, con su propia serie correlativa. Este campo
                  queda solo para anotar el del talonario de papel
                  cuando la planilla venía de ahí. */}
              <Campo
                etiqueta="N° del talonario de papel"
                ayuda="Opcional. El número de la plataforma se asigna solo al registrar."
              >
                <input value={form.numeroDocumento || ''}
                       onChange={(e) => set('numeroDocumento', e.target.value)}
                       placeholder="solo si viene de un talonario"
                       style={estiloInput} />
              </Campo>
            </Card>
            <PlanillaMovilidad topeDiario={tope} onCambio={setLineasMovilidad} />
          </>
        )}

        {/* ── Recibo AD ── */}
        {esRecibo && (
          <ReciboAd
            datos={form}
            onCambio={(d) => setForm((f) => ({ ...f, ...d }))}
            padron={padron}
          />
        )}

        {/* ── Sin comprobante ── */}
        {tipoDoc === 'sin_comprobante' && (
          <Card style={{ marginBottom: 16 }}>
            <Campo etiqueta="Fecha">
              <input type="date" value={form.fecha}
                     onChange={(e) => set('fecha', e.target.value)} style={estiloInput} />
            </Campo>
            <Campo etiqueta="Importe">
              <input inputMode="decimal" value={form.total}
                     onChange={(e) => set('total', e.target.value)}
                     style={{ ...estiloInput, fontWeight: 700 }} />
            </Campo>
            <Campo etiqueta="Qué se pagó">
              <input value={form.concepto} onChange={(e) => set('concepto', e.target.value)}
                     style={estiloInput} />
            </Campo>
          </Card>
        )}

        {/* ── Imputación, común a todos ── */}
        <Card style={{ marginBottom: 16 }}>
          {!esRecibo && !esMovilidad && (
            <Campo etiqueta="Concepto">
              <input value={form.concepto} onChange={(e) => set('concepto', e.target.value)}
                     placeholder="Repuesto para bomba de recirculación" style={estiloInput} />
            </Campo>
          )}

          {/* ── Quién consumió el gasto ───────────────────────────────
              Quien rinde no siempre es quien gastó. Dulce administra un
              encargo y rinde, pero el combustible lo consumió el técnico
              de obra. Sin este dato todo queda imputado a quien rinde, y
              no hay forma de saber en qué se fue el dinero por persona.

              Se puede elegir del padrón o escribir el nombre: la mayoría
              del personal de obra no tiene ficha. */}
          <Campo
            etiqueta="Quién consumió el gasto"
            ayuda={form.consumidorNombre
              ? 'Elige del padrón o escribe el nombre. Si lo gastaste tú, déjalo vacío.'
              : `Vacío = lo consumiste tú (${usuario.nombre}). Llénalo si lo gastó otra persona.`}
          >
            <input
              list="padron-consumidor"
              value={form.consumidorNombre || ''}
              onChange={(e) => {
                const v = e.target.value;
                const p = (padron || []).find((x) => x.nombre === v);
                setForm((f) => ({
                  ...f,
                  consumidorNombre: v,
                  // Si está en el padrón se guarda el id: así el nombre
                  // se resuelve de ahí aunque después se corrija.
                  consumidorId: p ? p.id : null,
                }));
              }}
              placeholder="Apellidos y nombres · opcional"
              style={estiloInput}
            />
            <datalist id="padron-consumidor">
              {(padron || []).map((p) => (
                <option key={p.id} value={p.nombre}>
                  {p.cargo ? `${p.cargo}` : `DNI ${p.dni}`}
                </option>
              ))}
            </datalist>
          </Campo>

          {esComprobante && (
            <Campo etiqueta="Cuenta contable">
              {/* Al elegir a mano se marca la cuenta como tocada, para que
                  un escaneo posterior no la pise con la aprendida. */}
              <select
                value={form.cuentaPcge}
                onChange={(e) => setForm((f) => ({
                  ...f, cuentaPcge: e.target.value, cuentaTocada: true,
                }))}
                style={estiloInput}>
                {cuentasDe(catalogos).map(([c, n]) => <option key={c} value={c}>{c} — {n}</option>)}
              </select>
            </Campo>
          )}

          <Campo etiqueta="Orden de venta">
            <select value={form.ovId} onChange={(e) => set('ovId', e.target.value)}
                    style={estiloInput}>
              <option value="">No corresponde a una OV</option>
              {ovs.map((o) => (
                <option key={o.id} value={o.id}>{o.id} — {o.clienteAlias}</option>
              ))}
            </select>
          </Campo>

          {!form.ovId && (
            <>
              <Campo etiqueta="Entonces, ¿a qué responde?"
                     ayuda="Contabilidad necesita saber a qué imputar el gasto.">
                <select value={form.motivoSinOv || ''}
                        onChange={(e) => set('motivoSinOv', e.target.value)}
                        style={estiloInput}>
                  <option value="">Elegir…</option>
                  {(catalogos?.motivosSinOv || []).map((m) => (
                    <option key={m.valor} value={m.valor}>{m.texto}</option>
                  ))}
                </select>
              </Campo>
              <Campo etiqueta="Orden de compra" ayuda="Opcional.">
                <input value={form.ocId || ''} onChange={(e) => set('ocId', e.target.value)}
                       placeholder="OC-2026-014" style={estiloInput} />
              </Campo>
            </>
          )}

          {encargosAplicables.length > 0 && !esRecibo && (
            <Campo etiqueta="Liquidar contra encargo"
                   ayuda="Si eliges uno, el monto se descuenta de tu saldo por rendir.">
              <select value={form.encargoLiquidadoId}
                      onChange={(e) => set('encargoLiquidadoId', e.target.value)}
                      style={estiloInput}>
                <option value="">No liquidar</option>
                {encargosAplicables.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.id} — saldo {fmt.money(e.saldo, e.moneda)}
                  </option>
                ))}
              </select>
            </Campo>
          )}
        </Card>

        {/* ── Foto ── */}
        <Card style={{ marginBottom: 16 }}>
          <Campo etiqueta={esMovilidad ? 'Foto de la planilla firmada' : 'Foto del comprobante'}
                 ayuda="Queda anexada al gasto. Máximo 6 MB.">
            <input type="file" accept="image/*,application/pdf"
                   onChange={elegirArchivo} style={{ ...estiloInput, padding: 8 }} />
          </Campo>
          {adjunto && (
            <div style={{ fontSize: 12, color: T.ok, fontWeight: 600 }}>
              Adjunto: {adjunto.nombre} ({Math.round(adjunto.bytes / 1024)} KB)
            </div>
          )}
        </Card>

        {/* Sin tipo de cambio el servidor rechaza la captura. Decirlo
            aquí evita que llene todo el formulario y lo pierda. */}
        {faltaTipoCambio && tipoDoc === 'comprobante' && (
          <Aviso tipo="warn">
            Falta el tipo de cambio: el gasto está en {form.moneda} y el
            libro de compras va en soles.
          </Aviso>
        )}

        <Boton type="submit" variante="acento"
               disabled={ocupado || (faltaTipoCambio && tipoDoc === 'comprobante')}
               style={{ width: '100%', padding: 14, fontSize: 15 }}>
          {ocupado ? 'Guardando…' : enLinea ? 'Enviar gasto' : 'Guardar sin señal'}
        </Boton>
      </form>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// MIS GASTOS
// ════════════════════════════════════════════════════════════════════
export function MisGastos({ usuario }) {
  const [gastos, setGastos] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [fallo, setFallo] = useState('');
  const [imprimir, setImprimir] = useState(null);
  const [abierto, setAbierto] = useState(null);

  useEffect(() => {
    setFallo('');
    api.gastos(filtro ? { estado: filtro } : {})
      .then((r) => setGastos(r.gastos || []))
      .catch((err) => { setFallo(mensajeDeError(err)); setGastos([]); });
  }, [filtro]);

  if (!gastos) return <Cargando />;

  const mios = usuario.rol === 'operativo'
    ? gastos
    : gastos.filter((g) => g.usuarioId === usuario.id);

  return (
    <>
      <Titulo sub="Todo lo que has capturado y en qué estado está">Mis gastos</Titulo>

      {fallo && <Aviso tipo="bad">{fallo}</Aviso>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['', 'Todos'], ['enviado', 'Por aprobar'], ['observado', 'Observados'],
          ['aprobado', 'Aprobados'], ['contabilizado', 'En SAP']].map(([v, t]) => (
          <button key={v} onClick={() => setFiltro(v)} style={{
            padding: '6px 13px', borderRadius: 16, fontSize: 13, cursor: 'pointer',
            border: `1px solid ${filtro === v ? T.navy : T.border}`,
            background: filtro === v ? T.navy : T.paper,
            color: filtro === v ? '#fff' : T.text,
          }}>
            {t}
          </button>
        ))}
      </div>

      {mios.length === 0
        ? <Vacio>No hay gastos con ese filtro.</Vacio>
        : <ListaGastos gastos={mios} alTocar={(g) => setAbierto(g)} />}

      {abierto && (
        <DetalleGasto
          gasto={abierto} usuario={usuario}
          onCerrar={() => setAbierto(null)}
          onGuardado={async () => {
            setAbierto(null);
            setFallo('');
            const r = await api.gastos(filtro ? { estado: filtro } : {}).catch(() => null);
            if (r) setGastos(r.gastos || []);
          }}
          onImprimir={() => { setImprimir(abierto); setAbierto(null); }}
        />
      )}

      {imprimir && (
        <DocumentoImprimible
          entidad="gasto" id={imprimir.id}
          onCerrar={() => setImprimir(null)}
        />
      )}
    </>
  );
}

export function ListaGastos({ gastos, alTocar }) {
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {gastos.map((g) => (
        <Card key={g.id} onClick={alTocar ? () => alTocar(g) : undefined} style={{ padding: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                <Chip estado={g.estado} />
                {g.ovId && (
                  <span style={{ fontSize: 11, color: T.muted }}>{g.ovId}</span>
                )}
              </div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {g.concepto || g.proveedorRazon || 'Gasto'}
              </div>
              <div style={{ color: T.muted, fontSize: 12, marginTop: 2 }}>
                {fmt.fecha(g.fecha)}
                {g.comprobanteSerie && ` · ${g.comprobanteSerie}-${g.comprobanteCorrelativo}`}
                {g.sapDocNum && ` · SAP ${g.sapDocNum}`}
              </div>
              {alTocar && ['recibo_ad', 'planilla_movilidad'].includes(g.tipoDocumento) && (
                <div style={{ fontSize: 11, color: T.brass, marginTop: 5 }}>
                  Imprimir y firmar
                </div>
              )}
              {g.observacion && g.estado === 'observado' && (
                <div style={{ fontSize: 12, color: T.warn, marginTop: 6 }}>
                  {g.observacion}
                </div>
              )}
            </div>
            <div style={{
              fontFamily: serif, fontSize: 18, fontWeight: 600,
              color: T.navy, whiteSpace: 'nowrap',
            }}>
              {fmt.money(g.total, g.moneda)}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}


/**
 * Traduce un fallo de la API a algo que el usuario pueda accionar.
 * Antes cualquier error dejaba la pantalla en blanco sin explicacion.
 */
export function mensajeDeError(err) {
  const codigo = err?.detalle?.error || err?.message;

  if (codigo === 'migracion_pendiente') {
    return 'La base de datos está desactualizada: falta ejecutar una actualización. Avisa a IT.';
  }
  if (codigo === 'sin_respuesta') {
    return 'El servidor no respondió a tiempo. Vuelve a intentarlo en un momento.';
  }
  if (codigo === 'sin_conexion') {
    return 'Sin conexión con el servidor. Revisa tu internet.';
  }
  if (err?.status === 403) return 'No tienes permiso para ver esto.';

  // El servidor casi siempre dice qué pasó. Descartarlo y mostrar un
  // mensaje genérico es lo que nos tuvo adivinando varias veces.
  if (typeof err?.detalle?.detalle === 'string' && err.detalle.detalle) {
    return err.detalle.detalle;
  }
  if (codigo && codigo !== 'error_interno') {
    return `No se pudo cargar la información (${codigo}).`;
  }
  return 'No se pudo cargar la información. Inténtalo otra vez.';
}


// ════════════════════════════════════════════════════════════════════
// Detalle de un gasto, con corrección
//
// Hasta ahora un concepto mal escrito dejaba el gasto atrapado: había
// que rechazarlo y capturarlo de nuevo. Lo que se puede corregir
// depende del estado, porque un gasto aprobado tiene un aprobador
// detrás y uno contabilizado tiene un asiento.
// ════════════════════════════════════════════════════════════════════
export function DetalleGasto({ gasto, usuario, onCerrar, onGuardado, onImprimir }) {
  const [editando, setEditando] = useState(false);
  const [f, setF] = useState({
    concepto: gasto.concepto || '',
    cuentaPcge: gasto.cuentaPcge || '',
    ovId: gasto.ovId || '',
    motivoSinOv: gasto.motivoSinOv || '',
    proveedorRazon: gasto.proveedorRazon || '',
    total: String(gasto.total || ''),
    fecha: (gasto.fecha || '').slice(0, 10),
  });
  const [ovs, setOvs] = useState([]);
  const [catalogos, setCat] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  useEffect(() => {
    if (!editando) return;
    Promise.all([api.ovs(true), api.catalogos()])
      .then(([o, c]) => { setOvs(o.ovs || []); setCat(c); })
      .catch(() => {});
  }, [editando]);

  const esMio = gasto.usuarioId === usuario.id;
  const esDireccion = usuario.rol === 'director';

  // Qué se puede tocar según el estado
  const bloqueo = (() => {
    if (gasto.estado === 'contabilizado') {
      return 'Ya está contabilizado en SAP. Para corregirlo hay que hacer el ajuste allá.';
    }
    if (gasto.estado === 'aprobado' && !esDireccion) {
      return 'Ya fue aprobado. Solo la dirección puede corregirlo.';
    }
    if (!esMio && !esDireccion) {
      return 'Solo quien lo capturó puede corregirlo.';
    }
    return null;
  })();

  const importeBloqueado = gasto.estado === 'aprobado';

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const guardar = async () => {
    setOcupado(true);
    try {
      const cambios = {
        concepto: f.concepto,
        cuentaPcge: f.cuentaPcge,
        proveedorRazon: f.proveedorRazon || null,
        ovId: f.ovId || null,
        motivoSinOv: f.ovId ? null : (f.motivoSinOv || null),
        fecha: f.fecha,
      };
      if (!importeBloqueado && Number(f.total) > 0) {
        cambios.total = Number(f.total);
        // El desglose se recalcula: si cambia el total y no el IGV,
        // el asiento saldría descuadrado.
        cambios.subtotal = Math.round((Number(f.total) / 1.18) * 100) / 100;
        cambios.igv = Math.round((Number(f.total) - cambios.subtotal) * 100) / 100;
      }
      const r = await api.editarGasto(gasto.id, cambios);
      if (r.volvioACola) {
        setAviso({ tipo: 'ok', texto: 'Corregido. Vuelve a la cola de aprobación.' });
        setTimeout(onGuardado, 1400);
      } else {
        onGuardado();
      }
    } catch (err) {
      setAviso({ tipo: 'bad', texto: err.detalle?.detalle || 'No se pudo guardar.' });
      setOcupado(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(10,15,26,.55)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }} onClick={onCerrar}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: T.cream, borderRadius: '14px 14px 0 0',
        width: '100%', maxWidth: 560, maxHeight: '92vh',
        overflowY: 'auto', padding: '18px 16px 26px',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', gap: 12, marginBottom: 14,
        }}>
          <div style={{ minWidth: 0 }}>
            <Chip estado={gasto.estado} />
            <div style={{ fontSize: 17, fontWeight: 600, marginTop: 6 }}>
              {gasto.concepto || 'Sin concepto'}
            </div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>
              {gasto.id} · {fmt.fecha(gasto.fecha)}
            </div>
          </div>
          <div style={{
            fontFamily: serif, fontSize: 22, fontWeight: 700,
            color: T.navy, whiteSpace: 'nowrap',
          }}>
            {fmt.money(gasto.total, gasto.moneda)}
          </div>
        </div>

        {aviso && <Aviso tipo={aviso.tipo}>{aviso.texto}</Aviso>}

        {gasto.observacion && gasto.estado === 'observado' && (
          <Aviso tipo="warn">{gasto.observacion}</Aviso>
        )}

        {!editando ? (
          <>
            <Card style={{ marginBottom: 14 }}>
              {[
                ['Proveedor', gasto.proveedorRazon],
                ['RUC', gasto.proveedorRuc],
                ['Comprobante', gasto.comprobanteSerie
                  ? `${gasto.comprobanteSerie}-${gasto.comprobanteCorrelativo}`
                  : gasto.numeroDocumento],
                ['Cuenta', gasto.cuentaPcge],
                ['Orden de venta', gasto.ovId || 'Sin OV'],
                ['Centro', gasto.centro],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} style={{
                  display: 'flex', justifyContent: 'space-between',
                  gap: 12, padding: '5px 0', fontSize: 13,
                }}>
                  <span style={{ color: T.muted }}>{k}</span>
                  <span style={{ textAlign: 'right' }}>{v}</span>
                </div>
              ))}
            </Card>

            {bloqueo && <Aviso tipo="info">{bloqueo}</Aviso>}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {!bloqueo && (
                <Boton variante="acento" onClick={() => setEditando(true)} style={{ flex: 1 }}>
                  Corregir
                </Boton>
              )}
              {['recibo_ad', 'planilla_movilidad'].includes(gasto.tipoDocumento) && (
                <Boton variante="suave" onClick={onImprimir} style={{ flex: 1 }}>
                  Imprimir
                </Boton>
              )}
              <Boton variante="suave" onClick={() => setEliminando(true)}
                     style={{ flex: 1, color: T.bad }}>
                Eliminar
              </Boton>
              <Boton variante="suave" onClick={onCerrar} style={{ flex: 1 }}>
                Cerrar
              </Boton>
            </div>

            {eliminando && (
              <ConfirmarEliminar
                titulo={`el gasto ${gasto.id}`}
                descripcion={`${gasto.concepto || 'Sin concepto'} · ${fmt.money(gasto.total, gasto.moneda)}`}
                consultar={() => api.gastoEliminable(gasto.id)}
                ejecutar={(motivo) => api.borrarGasto(gasto.id, motivo)}
                onCerrar={() => setEliminando(false)}
                onHecho={() => { setEliminando(false); onGuardado(); }}
              />
            )}
          </>
        ) : (
          <>
            <Campo etiqueta="Concepto">
              <input value={f.concepto} onChange={(e) => set('concepto', e.target.value)}
                     style={estiloInput} />
            </Campo>

            <Campo etiqueta="Proveedor">
              <input value={f.proveedorRazon}
                     onChange={(e) => set('proveedorRazon', e.target.value)}
                     style={estiloInput} />
            </Campo>

            <Campo etiqueta="Cuenta contable">
              <select value={f.cuentaPcge} onChange={(e) => set('cuentaPcge', e.target.value)}
                      style={estiloInput}>
                {cuentasDe(catalogos).map(([c, t]) => (
                  <option key={c} value={c}>{c} · {t}</option>
                ))}
              </select>
            </Campo>

            <Campo etiqueta="Orden de venta">
              <select value={f.ovId} onChange={(e) => set('ovId', e.target.value)}
                      style={estiloInput}>
                <option value="">Sin OV</option>
                {ovs.map((o) => (
                  <option key={o.id} value={o.id}>{o.id} — {o.clienteAlias}</option>
                ))}
              </select>
            </Campo>

            {!f.ovId && (
              <Campo etiqueta="Motivo sin OV">
                <select value={f.motivoSinOv} onChange={(e) => set('motivoSinOv', e.target.value)}
                        style={estiloInput}>
                  <option value="">Elegir…</option>
                  {(catalogos?.motivosSinOv || []).map((m) => (
                    <option key={m.valor} value={m.valor}>{m.texto}</option>
                  ))}
                </select>
              </Campo>
            )}

            <Campo etiqueta="Fecha">
              <input type="date" value={f.fecha} onChange={(e) => set('fecha', e.target.value)}
                     style={estiloInput} />
            </Campo>

            <Campo
              etiqueta="Importe"
              ayuda={importeBloqueado
                ? 'No se puede cambiar después de aprobado: el aprobador decidió sobre esa cifra.'
                : 'Al cambiarlo se recalcula el IGV al 18%.'}
            >
              <input inputMode="decimal" value={f.total} disabled={importeBloqueado}
                     onChange={(e) => set('total', e.target.value)}
                     style={{
                       ...estiloInput,
                       background: importeBloqueado ? T.warm : T.paper,
                       color: importeBloqueado ? T.muted : T.text,
                     }} />
            </Campo>

            {gasto.estado === 'observado' && (
              <Aviso tipo="info">
                Al guardar vuelve a la cola de aprobación.
              </Aviso>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <Boton variante="suave" onClick={() => setEditando(false)}
                     disabled={ocupado} style={{ flex: 1 }}>
                Cancelar
              </Boton>
              <Boton variante="acento" onClick={guardar} disabled={ocupado} style={{ flex: 1.3 }}>
                {ocupado ? 'Guardando…' : 'Guardar'}
              </Boton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
